// Vehicle Makes Dropdown — renders a searchable grid of vehicle makes (name only,
// no logos), each linking to /parts-finder?make=<name>. Typically embedded inside a
// Bootstrap dropdown menu, so mounting is deferred until the container is both in the
// DOM and actually visible (menu opened).
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-vehicle-makes-dropdown] — the container (may appear multiple times)
//
// apiKey / base URL come from shared config (via apiJson); there is no per-instance
// config to move off the old <script data-*> tag.

import { getTopDocument } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';

var CONTAINER_SELECTOR = '[data-parts-vehicle-makes-dropdown]';

// ---------------------------------------------------------------------------
// Fetch vehicle makes from the Parts Finder filters API.
// Response shape: { makes: string[], models: {...}, ... }
// apiJson resolves to null on any non-OK / network error (→ treated as error).
// ---------------------------------------------------------------------------
function fetchMakes(callback) {
  // Only the make list is needed here — use the light index endpoint (~67KB)
  // rather than the full vehicle tree (~540KB).
  apiJson('/api/filters/vehicle/index', null, null).then(function (data) {
    if (!data) { callback(new Error('Could not load vehicle makes'), []); return; }
    var makes = Array.isArray(data.makes) ? data.makes : [];
    makes = makes.filter(function (m) { return m && m.trim(); });
    makes.sort(function (a, b) { return a.localeCompare(b); });
    callback(null, makes);
  });
}

// ---------------------------------------------------------------------------
// Build a single make card. Stored name in dataset for query matching.
// ---------------------------------------------------------------------------
function buildMakeCard(make) {
  var doc = getTopDocument();
  var href = '/parts-finder?sort=relevance&make=' + encodeURIComponent(make);
  var card = doc.createElement('a');
  card.className = 'pf-make-card';
  card.href = href;
  card.dataset.makeName = make.toLowerCase();
  var nameEl = doc.createElement('div');
  nameEl.className = 'pf-make-name';
  nameEl.textContent = make;
  card.appendChild(nameEl);
  return card;
}

// ---------------------------------------------------------------------------
// Initial render — build all cards once.
// ---------------------------------------------------------------------------
function renderMakes(grid, allMakes) {
  grid.innerHTML = '';
  allMakes.forEach(function (make) {
    grid.appendChild(buildMakeCard(make));
  });
}

// ---------------------------------------------------------------------------
// Apply search state — mutate CSS classes only, no DOM rebuild.
// ---------------------------------------------------------------------------
function applyMakeQuery(grid, countBadge, q, totalCount) {
  var cards = grid.querySelectorAll('.pf-make-card');
  var matchCount = 0;
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var name = card.dataset.makeName || '';
    var isMatch = !q || name.indexOf(q) !== -1;
    if (isMatch) matchCount++;
    card.classList.toggle('pf-match', !!q && isMatch);
    card.classList.toggle('pf-dimmed', !!q && !isMatch);
  }
  if (q) {
    countBadge.textContent = matchCount + ' of ' + totalCount + ' match' + (matchCount !== 1 ? 'es' : '');
    countBadge.classList.add('has-query');
  } else {
    countBadge.textContent = totalCount + ' make' + (totalCount !== 1 ? 's' : '');
    countBadge.classList.remove('has-query');
  }
}

// ---------------------------------------------------------------------------
// Build scaffold with skeleton loader grid
// ---------------------------------------------------------------------------
function buildScaffold(container) {
  var doc = getTopDocument();

  var wrap = doc.createElement('div');
  wrap.className = 'pf-makes-wrap';
  wrap.id = 'pf-makes-' + Math.random().toString(36).substr(2, 9);

  // Wrap all skeleton elements in one container so a single .remove() clears it
  var skelContainer = doc.createElement('div');

  // Skeleton search bar
  var skelBar = doc.createElement('div');
  skelBar.className = 'pf-makes-skel-bar';
  var skelInput = doc.createElement('div');
  skelInput.className = 'pf-makes-skel-input';
  var skelBadge = doc.createElement('div');
  skelBadge.className = 'pf-makes-skel-badge';
  skelBar.appendChild(skelInput);
  skelBar.appendChild(skelBadge);
  skelContainer.appendChild(skelBar);

  // Skeleton card grid
  var skelGrid = doc.createElement('div');
  skelGrid.className = 'pf-makes-skeleton-grid';
  for (var i = 0; i < 48; i++) {
    var skel = doc.createElement('div');
    skel.className = 'pf-makes-skel';
    skelGrid.appendChild(skel);
  }
  skelContainer.appendChild(skelGrid);
  wrap.appendChild(skelContainer);

  container.appendChild(wrap);
  return { wrap: wrap, skeleton: skelContainer };
}

// ---------------------------------------------------------------------------
// Wire up search — renders all cards once, then mutates classes on keyup.
// ---------------------------------------------------------------------------
function buildInteractiveUI(wrap, skeleton, allMakes) {
  var doc = getTopDocument();
  skeleton.remove();

  if (allMakes.length === 0) {
    var empty = doc.createElement('div');
    empty.className = 'pf-makes-empty';
    empty.textContent = 'No vehicle makes found.';
    wrap.appendChild(empty);
    return;
  }

  // Search row
  var searchRow = doc.createElement('div');
  searchRow.className = 'pf-makes-search-row';

  var searchInput = doc.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'pf-makes-search-input';
  searchInput.placeholder = 'Search vehicle makes…';
  searchInput.setAttribute('aria-label', 'Search vehicle makes');

  var countBadge = doc.createElement('span');
  countBadge.className = 'pf-makes-count-badge';
  countBadge.textContent = allMakes.length + ' makes';

  searchRow.appendChild(searchInput);
  searchRow.appendChild(countBadge);
  wrap.appendChild(searchRow);

  // Grid — built once; search mutates CSS classes only
  var grid = doc.createElement('div');
  grid.className = 'pf-makes-grid';
  wrap.appendChild(grid);
  renderMakes(grid, allMakes);

  // Search: grey out mismatches + float matches to front via CSS order
  searchInput.addEventListener('input', function () {
    var q = searchInput.value.trim().toLowerCase();
    applyMakeQuery(grid, countBadge, q, allMakes.length);
  });
}

// ---------------------------------------------------------------------------
// Visibility helpers
// ---------------------------------------------------------------------------
// An element counts as "visible enough to build into" when it has layout
// boxes (offsetParent is non-null) AND a non-zero rendered width. Bootstrap
// dropdowns with `display:none` fail the first check; menus that are
// transitioning open with width 0 fail the second.
function isVisible(el) {
  if (!el || !el.isConnected) return false;
  var win = (el.ownerDocument && el.ownerDocument.defaultView) || window;
  // Walk up checking computed display/visibility — getBoundingClientRect
  // returns non-zero for `visibility:hidden`, and offsetParent is null
  // even for visible `position:fixed` elements, so neither alone is
  // enough. Limit the walk so an outlier doesn't make this expensive.
  var node = el;
  var hops = 0;
  while (node && node.nodeType === 1 && hops < 50) {
    var cs = win.getComputedStyle(node);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    node = node.parentElement;
    hops++;
  }
  var r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

// Wait for the container to be visible (laid out with non-zero box). Calls
// `cb` once. Uses IntersectionObserver to defer work until the Bootstrap
// menu actually opens; falls back to a periodic visibility poll on browsers
// without IO (or when the container is inside a transform/clipped ancestor
// that confuses IO ratio calculations).
function whenVisible(el, cb) {
  if (isVisible(el)) return cb();

  var fired = false;
  var fire = function () {
    if (fired) return;
    fired = true;
    if (io) io.disconnect();
    if (pollId) clearInterval(pollId);
    cb();
  };

  var io = null;
  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && entries[i].intersectionRatio > 0) {
          fire();
          return;
        }
      }
    }, { root: null, threshold: 0.01 });
    io.observe(el);
  }

  // Polling fallback — catches cases where the menu animates in via
  // `display:none → display:block` (IO doesn't always fire reliably
  // on rapid visibility flips inside transforming ancestors).
  var pollId = setInterval(function () {
    if (isVisible(el)) fire();
  }, 300);
}

// ---------------------------------------------------------------------------
// Fetch cache — so the widget can be built into multiple instances / re-opened
// menus without re-hitting /api/filters/vehicle every time.
// ---------------------------------------------------------------------------
var _makesCache = null;
var _makesCachePromise = null;

function getMakes(callback) {
  if (_makesCache) return callback(null, _makesCache);
  if (_makesCachePromise) {
    _makesCachePromise.then(function (m) { callback(null, m); }, function (e) { callback(e, []); });
    return;
  }
  _makesCachePromise = new Promise(function (resolve, reject) {
    fetchMakes(function (err, makes) {
      if (err) { _makesCachePromise = null; reject(err); return; }
      _makesCache = makes;
      resolve(makes);
    });
  });
  _makesCachePromise.then(function (m) { callback(null, m); }, function (e) { callback(e, []); });
}

// Mount the widget into a container. Idempotent — if a previous run already
// built UI into the same container, the second call is a no-op (lets us
// safely re-call when the menu reopens).
function mount(container) {
  if (container.querySelector('.pf-makes-wrap')) return; // already mounted

  var refs = buildScaffold(container);

  getMakes(function (err, makes) {
    if (err) {
      console.error('VehicleMakesDropdown: fetch error', err);
      refs.skeleton.remove();
      var errEl = getTopDocument().createElement('div');
      errEl.className = 'pf-makes-empty';
      errEl.textContent = 'Could not load vehicle makes.';
      refs.wrap.appendChild(errEl);
      return;
    }
    buildInteractiveUI(refs.wrap, refs.skeleton, makes);
  });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
// Containers we've already begun watching, so re-scans (from the DOM observer)
// don't attach duplicate visibility observers to the same element.
var _watched = null;

export function init() {
  var doc = getTopDocument();
  if (typeof WeakSet === 'function' && !_watched) _watched = new WeakSet();

  function watch(container) {
    if (_watched) {
      if (_watched.has(container)) return;
      _watched.add(container);
    }
    // Bootstrap menus are typically `display:none` until opened — defer the
    // actual mount until the container has measurable layout boxes. If it's
    // already visible (page-embedded, not in a menu) we mount immediately.
    whenVisible(container, function () { mount(container); });
  }

  function scan() {
    var containers = doc.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < containers.length; i++) watch(containers[i]);
  }

  scan();

  // The container(s) may be injected into the DOM later (e.g. a dropdown menu
  // rendered on demand). Watch for additions and re-scan, bounded to ~60s as a
  // safety cap so the observer can't leak.
  if (typeof MutationObserver === 'function') {
    var mo = new MutationObserver(function () { scan(); });
    try { mo.observe(doc.documentElement, { childList: true, subtree: true }); } catch (e) { /* ignore */ }
    setTimeout(function () { try { mo.disconnect(); } catch (e) { /* ignore */ } }, 60000);
  }
}

export var id = 'vehicle-makes-dropdown';
